import {
  IVacationCarryOverRequestActions,
  IUpdateVacationCarryOverStatePayload,
  VacationCarryOverContext,
  IVacationCarryOverRequestCommentFetchActionPayload,
  IVacationCarryOverRequestCommentUpdateActionPayload,
  IVacationCarryOverAttachmentCommentAttachmentRemoveActionPayload,
  IVacationCarryOverAttachmentCommentAttachmentAddActionPayload
} from './actions.types';
import {
  IMentionAttachmentCommentData,
  IUserData,
  IVacationCarryOverRequestCommentData,
  IVacationCarryOverRequestData
} from '@/models';
import {
  IFetchVacationCarryOverRequestsFilterParams,
  IVacationCarryOverRequestPaginatedListData
} from '@/services/vacation-carry-over-request';
import { ITransition } from '@/models/transition';

export const actions: IVacationCarryOverRequestActions = {
  fetchVacationCarryOverRequests(
    context: VacationCarryOverContext,
    payload: IFetchVacationCarryOverRequestsFilterParams
  ): Promise<void> {
    const promise = this.$services.vacationCarryOverRequest.fetchVacationCarryOverRequests(
      payload
    );
    context.commit('SET_LAST_PROMISE', promise);
    return promise.then(
      (
        vacationCarryOverRequests: IVacationCarryOverRequestPaginatedListData
      ) => {
        if (promise == context.state.lastPromise) {
          context.commit('SET_COUNT', vacationCarryOverRequests.count);
          context.commit(
            'SET_VACATION_CARRY_OVER_REQUESTS',
            vacationCarryOverRequests.results
          );
        }
        context.commit('SET_LAST_PROMISE', null);
      }
    );
  },

  fetchVacationCarryOverRequest(
    context: VacationCarryOverContext,
    payload: number
  ): Promise<void> {
    return this.$services.vacationCarryOverRequest
      .fetchVacationCarryOverRequest(payload)
      .then((vacationCarryOverRequest: IVacationCarryOverRequestData) => {
        const index = context.state.vacationCarryOverRequests.findIndex(
          (v: IVacationCarryOverRequestData) =>
            v.id === vacationCarryOverRequest.id
        );
        if (index === -1) {
          context.commit(
            'ADD_VACATION_CARRY_OVER_REQUEST',
            vacationCarryOverRequest
          );
        } else {
          context.commit(
            'UPDATE_VACATION_CARRY_OVER_REQUEST',
            vacationCarryOverRequest
          );
        }
      });
  },

  createVacationCarryOverRequest(
    context: VacationCarryOverContext,
    payload: IVacationCarryOverRequestData
  ): Promise<void> {
    return this.$services.vacationCarryOverRequest
      .createVacationCarryOverRequest(payload)
      .then((vacationCarryOverRequest: IVacationCarryOverRequestData) =>
        context.commit(
          'ADD_VACATION_CARRY_OVER_REQUEST',
          vacationCarryOverRequest
        )
      );
  },

  updateVacationCarryOverRequest(
    context: VacationCarryOverContext,
    payload: IVacationCarryOverRequestData
  ): Promise<void> {
    return this.$services.vacationCarryOverRequest
      .updateVacationCarryOverRequest(payload)
      .then((vacationCarryOverRequest: IVacationCarryOverRequestData) => {
        const index = context.state.vacationCarryOverRequests.findIndex(
          (v: IVacationCarryOverRequestData) =>
            v.id === vacationCarryOverRequest.id
        );
        if (index === -1) {
          context.commit(
            'ADD_VACATION_CARRY_OVER_REQUEST',
            vacationCarryOverRequest
          );
        } else {
          context.commit(
            'UPDATE_VACATION_CARRY_OVER_REQUEST',
            vacationCarryOverRequest
          );
        }
      });
  },

  deleteVacationCarryOverRequest(
    context: VacationCarryOverContext,
    payload: number
  ): Promise<void> {
    return this.$services.vacationCarryOverRequest
      .deleteVacationCarryOverRequest(payload)
      .then(() => {
        context.commit('REMOVE_VACATION_CARRY_OVER_REQUEST', payload);
      });
  },

  fetchVacationCarryOverRequestActivities(
    context: VacationCarryOverContext,
    payload: number
  ): Promise<void> {
    return this.$services.vacationCarryOverRequest
      .fetchVacationCarryOverRequestActivities(payload)
      .then((activities: ITransition[]) => {
        context.commit(
          'SET_VACATION_CARRY_OVER_REQUEST_ACTIVITIES',
          activities
        );
      });
  },

  fetchVacationCarryOverRequestComments(
    context: VacationCarryOverContext,
    payload: number
  ): Promise<void> {
    return this.$services.vacationCarryOverRequest
      .fetchVacationCarryOverRequestComments(payload)
      .then((comments: IVacationCarryOverRequestCommentData[]) => {
        context.commit('SET_VACATION_CARRY_OVER_REQUEST_COMMENTS', comments);
      });
  },

  fetchVacationCarryOverRequestComment(
    context: VacationCarryOverContext,
    payload: IVacationCarryOverRequestCommentFetchActionPayload
  ): Promise<void> {
    return this.$services.vacationCarryOverRequest
      .fetchVacationCarryOverRequestComment(
        payload.overtimeId,
        payload.commentId
      )
      .then((comment: IVacationCarryOverRequestCommentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === comment.id
        );
        if (index === -1) {
          context.commit('ADD_VACATION_CARRY_OVER_REQUEST_COMMENT', comment);
        } else {
          context.commit('UPDATE_VACATION_CARRY_OVER_REQUEST_COMMENT', comment);
        }
      });
  },

  createVacationCarryOverRequestComment(
    context: VacationCarryOverContext,
    payload: IVacationCarryOverRequestCommentUpdateActionPayload
  ): Promise<void> {
    return this.$services.vacationCarryOverRequest
      .createVacationCarryOverRequestComment(
        payload.overtimeId,
        payload.comment
      )
      .then((comment: IVacationCarryOverRequestCommentData) => {
        context.commit(
          'SET_LAST_CREATED_VACATION_CARRY_OVER_REQUEST_COMMENT_ID',
          comment.id
        );
        context.commit('ADD_VACATION_CARRY_OVER_REQUEST_COMMENT', comment);
      });
  },

  updateVacationCarryOverRequestComment(
    context: VacationCarryOverContext,
    payload: IVacationCarryOverRequestCommentUpdateActionPayload
  ): Promise<void> {
    return this.$services.vacationCarryOverRequest
      .updateVacationCarryOverRequestComment(
        payload.overtimeId,
        payload.comment
      )
      .then((comment: IVacationCarryOverRequestCommentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === comment.id
        );
        if (index === -1) {
          context.commit('ADD_VACATION_CARRY_OVER_REQUEST_COMMENT', comment);
        } else {
          context.commit('UPDATE_VACATION_CARRY_OVER_REQUEST_COMMENT', comment);
        }
      });
  },

  deleteVacationCarryOverRequestComment(
    context: VacationCarryOverContext,
    payload: IVacationCarryOverRequestCommentFetchActionPayload
  ): Promise<void> {
    return this.$services.vacationCarryOverRequest
      .deleteVacationCarryOverRequestComment(
        payload.overtimeId,
        payload.commentId
      )
      .then(() => {
        context.commit(
          'REMOVE_VACATION_CARRY_OVER_REQUEST_COMMENT',
          payload.commentId
        );
      });
  },

  addVacationCarryOverRequestCommentAttachments(
    context: VacationCarryOverContext,
    payload: IVacationCarryOverAttachmentCommentAttachmentAddActionPayload
  ): Promise<void> {
    return this.$services.vacationCarryOverRequest
      .addVacationCarryOverRequestCommentAttachments(
        payload.overtimeId,
        payload.commentId,
        payload.attachments
      )
      .then((comment: IMentionAttachmentCommentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === payload.commentId
        );
        if (index === -1) {
          context.commit('ADD_VACATION_CARRY_OVER_REQUEST_COMMENT', comment);
        } else {
          context.commit('UPDATE_VACATION_CARRY_OVER_REQUEST_COMMENT', comment);
        }
      });
  },

  removeVacationCarryOverRequestCommentAttachment(
    context: VacationCarryOverContext,
    payload: IVacationCarryOverAttachmentCommentAttachmentRemoveActionPayload
  ): Promise<void> {
    return this.$services.vacationCarryOverRequest
      .removeVacationCarryOverRequestCommentAttachment(
        payload.overtimeId,
        payload.commentId,
        payload.attachmentId
      )
      .then(() => {
        const index = context.state.comments.findIndex(
          comment => comment.id === payload.commentId
        );

        if (index !== -1) {
          const comment = context.state.comments[index];

          const attachmentIndex = comment.attachments!.findIndex(
            attachment => attachment.id === payload.attachmentId
          );

          comment.attachments!.splice(attachmentIndex, 1);

          context.commit('UPDATE_VACATION_CARRY_OVER_REQUEST_COMMENT', comment);
        }
      });
  },

  // Mentions
  fetchVacationCarryOverRequestCommentsMentionableUsers(
    context: VacationCarryOverContext,
    payload: number
  ): Promise<void> {
    return this.$services.vacationCarryOverRequest
      .fetchVacationCarryOverRequestCommentsMentionableUsers(payload)
      .then((mentionableUsers: IUserData[]) => {
        context.commit('SET_MENTIONABLE_USERS', mentionableUsers);
      });
  },

  updateVacationCarryOverRequestState: function(
    context: VacationCarryOverContext,
    payload: IUpdateVacationCarryOverStatePayload
  ): Promise<void> {
    return this.$services.vacationCarryOverRequest
      .updateVacationCarryOverRequestState(payload)
      .then((vacationCarryOverRequest: IVacationCarryOverRequestData) => {
        const index = context.state.vacationCarryOverRequests.findIndex(
          (v: IVacationCarryOverRequestData) =>
            v.id === vacationCarryOverRequest.id
        );
        if (index === -1) {
          context.commit(
            'ADD_VACATION_CARRY_OVER_REQUEST',
            vacationCarryOverRequest
          );
        } else {
          context.commit(
            'UPDATE_VACATION_CARRY_OVER_REQUEST',
            vacationCarryOverRequest
          );
        }
      });
  }
};
